import React from "react";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeroSection from "../components/home/HeroSection";
import { acquisitionApi } from "../api/acquisition";
import { useAuth } from "../context/AuthContext";
import {
  ACQUISITION_EXPERIENCE_VERSION,
  acquisitionCampaign,
  acquisitionDeviceMetadata,
  acquisitionEventId,
  acquisitionSessionId,
  acquisitionVisitorId,
  saveAcquisitionDraft,
} from "../utils/acquisitionFunnel";

// BLOCK 3 · Minimal acquisition-first landing + anonymous/authenticated traffic classification.
// STATUS: STATIC_PASS. Runtime/responsive verification pending staging deploy.
// Existing sales sections remain untouched in source; this page simply stops mounting them.
export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [stats, setStats] = React.useState([]);
  const isAuthenticated = Boolean(user);
  const referralCode = String(searchParams.get("ref") || "").trim().toUpperCase();
  const sessionId = React.useMemo(() => acquisitionSessionId(), []);
  const visitorId = React.useMemo(() => acquisitionVisitorId(), []);

  React.useEffect(() => {
    acquisitionApi.publicStats().then((data) => setStats(Array.isArray(data?.stats) ? data.stats : [])).catch(() => setStats([]));
  }, []);

  React.useEffect(() => {
    // BLOCK 4 · Acquisition landing capture must not wait for /auth/me.
    // STATUS: STATIC_PASS · STAGING_PENDING.
    // WHY: the shortest visits are the easiest to lose. We create the anonymous
    // browser/session heartbeat immediately with audience=unknown, then classify
    // it in the separate auth-resolution effect below. Event ids keep StrictMode
    // remounts/idempotent retries from inflating the landing count.
    const campaign = acquisitionCampaign(searchParams);
    saveAcquisitionDraft({ sessionId, visitorId, referralCode, entryPoint: "home", campaign, experienceVersion: ACQUISITION_EXPERIENCE_VERSION });
    acquisitionApi.startSession({ sessionId, visitorId, entryPoint: "home", referralCode, campaign, audience: "unknown", experienceVersion: ACQUISITION_EXPERIENCE_VERSION }).catch(() => null);
    acquisitionApi.track({
      action: "acquisition_landing_view",
      sessionId,
      visitorId,
      eventId: `landing_${sessionId}`.slice(0, 60),
      metadata: { stepKey: "landing", stepIndex: 0, totalSteps: 8, progressPct: 0, entryPoint: "home", audience: "unknown", experienceVersion: ACQUISITION_EXPERIENCE_VERSION, ...acquisitionDeviceMetadata() },
    }).catch(() => null);
  }, [referralCode, searchParams, sessionId, visitorId]);

  React.useEffect(() => {
    // BLOCK 4 · Resolve traffic classification after auth without delaying capture.
    // STATUS: STATIC_PASS · STAGING_PENDING. The prospect record is the source of
    // truth used by analytics to exclude authenticated owner/staff test traffic.
    if (loading) return;
    const audience = isAuthenticated ? "authenticated" : "anonymous";
    const campaign = acquisitionCampaign(searchParams);
    acquisitionApi.startSession({
      sessionId,
      visitorId,
      entryPoint: "home",
      referralCode,
      campaign,
      audience,
      experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
    }).catch(() => null);
  }, [isAuthenticated, loading, referralCode, searchParams, sessionId, visitorId]);

  const enterFunnel = (entryPoint = "start", need = "") => {
    if (loading) return;
    if (isAuthenticated && entryPoint === "start" && !need) {
      navigate("/dashboard");
      return;
    }

    const firstFunnelStep = entryPoint === "what-is" ? "offer" : entryPoint === "quick-need" ? "business" : "intro";
    saveAcquisitionDraft({ sessionId, visitorId, referralCode, funnelEntryPoint: entryPoint, stepIndex: 0, stepKey: firstFunnelStep, offerIndex: 0, primaryNeed: need || "", primaryNeedSource: need ? "hero_quick" : "", experienceVersion: ACQUISITION_EXPERIENCE_VERSION });
    acquisitionApi.track({
      action: "acquisition_cta_clicked",
      sessionId,
      visitorId,
      eventId: acquisitionEventId("cta"),
      metadata: { stepKey: "landing", stepIndex: 0, totalSteps: 8, progressPct: 0, entryPoint, optionKey: need, audience: isAuthenticated ? "authenticated" : "anonymous", experienceVersion: ACQUISITION_EXPERIENCE_VERSION, ...acquisitionDeviceMetadata() },
    }).catch(() => null);

    // BLOCK 5 · Explicit intent event for the animated quick-entry cards.
    // STATUS: STATIC_PASS · STAGING_PENDING. The generic CTA event remains for
    // backwards-compatible funnel metrics; this event makes intent analysis clearer.
    if (need) {
      acquisitionApi.track({
        action: "acquisition_quick_need_selected",
        sessionId,
        visitorId,
        eventId: acquisitionEventId("quick"),
        metadata: { stepKey: "landing", stepIndex: 0, entryPoint, optionKey: need, intentSource: "hero_quick", audience: isAuthenticated ? "authenticated" : "anonymous", experienceVersion: ACQUISITION_EXPERIENCE_VERSION, ...acquisitionDeviceMetadata() },
      }).catch(() => null);
    }

    const params = new URLSearchParams();
    if (referralCode) params.set("ref", referralCode);
    if (entryPoint) params.set("entry", entryPoint);
    if (need) params.set("need", need);
    navigate(`/empecemos${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <Box component="main" sx={{ width: "100%", overflowX: "hidden", bgcolor: "#0B1016", color: "#F7F8FA" }}>
      <HeroSection
        stats={stats}
        onStartClick={() => enterFunnel("start")}
        onExploreClick={() => enterFunnel("what-is")}
        onQuickNeed={(need) => enterFunnel("quick-need", need)}
        primaryLabel={loading ? "Cargando..." : isAuthenticated ? "Abrir mi panel" : "Empezar"}
        isAuthenticated={isAuthenticated}
        userName={user?.name || ""}
      />
    </Box>
  );
}
